import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { get_ticket_list } from '../../redux/ticketSlice';
import {
  Box,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import store from '../../redux/store';
import GasCylinderLoader from '../../components/GasCylinderLoader';

const Support = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { ticket_list, loading, total_ticket_count } = useSelector((state) => state.tickets);
  const [rows, setRows] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [initialValue, setInitialValue] = React.useState(1);

  // 🔍 Search query state
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (location.state?.page && location.state?.rows && location.state?.initialValue) {
      setPage(location.state.page);
      setRows(location.state.rows);
      setInitialValue(location.state.initialValue);
      dispatch(get_ticket_list({
        page: location.state.page,
        rows: location.state.rows
      }));
    } else {
      dispatch(get_ticket_list({ page: page, rows: rows }));
    }
  }, []);
  
  useEffect(() => {
    dispatch(get_ticket_list({ page: page, rows: rows }));
  }, [dispatch, page, rows]);

  const onPrevious = () => {
    if (page > 1) {
      setPage(page - 1);
      setInitialValue(prev => Number(prev) - Number(rows));
    }
  };

  const onNext = () => {
    if (rows * page <= total_ticket_count) {
      setInitialValue(prev => Number(prev) + Number(rows));
      setPage(page + 1);
    }
  };

  const handleTicketClick = (ticket) => {
    navigate('/support/ticket-detail', {
      state: {
        ticketid: ticket.ticketid,
        page,
        rows,
        initialValue
      },
    });
  };

  // 🔍 Filter tickets based on Subject, Ticket ID, or Name
  const filteredTickets = ticket_list?.filter((ticket) => {
    const query = searchQuery.toLowerCase().trim();
    return (
      ticket.subject?.toLowerCase().includes(query) ||
      ticket.ticketid?.toString().toLowerCase().includes(query) ||
      ticket.name?.toLowerCase().includes(query)
    );
  });

  if (loading) return <GasCylinderLoader size='sm' loadingText='Loading Tickets'/>;

  return (
    <>
      <Card
        bg="#fff"
        paddingInline={5}
        paddingBlock={2}
        display="flex"
        flexDirection={["column", "column", "row"]}
        alignItems={["stretch", "stretch", "center"]}
        gap={3}
        flexWrap="wrap"
      >
        <Heading fontSize="xl" flexShrink={0}>
          Customer Support Tickets
        </Heading>

        <Spacer display={["none", "none", "block"]} />

        <Flex
          direction={["column", "row"]}
          gap={2}
          alignItems="center"
          justifyContent={["flex-start", "flex-end"]}
          flex="1"
          flexWrap="wrap"
        >
          <Select
            placeholder="Rows"
            size="md"
            w={["100%", "100px"]}
            value={rows}
            onChange={(e) => {
              setRows(e.target.value);
              setPage(1);
              setInitialValue(1);
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>

          <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "auto"]} maxW="sm">
            <Input
              placeholder="Search by Subject, Ticket ID or Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputRightElement>
              <FiSearch />
            </InputRightElement>
          </InputGroup>
        </Flex>
      </Card>

      {filteredTickets.length === 0 ? (
        <Card p={8} textAlign="center">
          <Text>No tickets found</Text>
        </Card>
      ) : (
        <>
          <SimpleGrid minChildWidth="xs" spacing="10px">
            {filteredTickets?.map((ticket) => (
              <Card
                key={ticket.ticketid}
                paddingInline={5}
                paddingBlock={2}
                cursor="pointer"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                _hover={{ boxShadow: "md", transform: "translateY(-3px)" }}
                transition="all 0.2s ease"
                onClick={() => handleTicketClick(ticket)}
              >
                {/* Header - Subject */}
                <Flex justify="space-between" align="start" mb={2}>
                  <Heading fontSize="16px" fontWeight="bold" color="#007bc9" noOfLines={2}>
                    {ticket.subject || "NA"}
                  </Heading>
                </Flex>

                {/* Ticket Info */}
                <Box mt={1} flex="1">
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="sm" color="gray.600">
                      Ticket #
                    </Text>
                    <Text fontSize="sm" color="gray.800" fontWeight="medium">
                      {ticket.ticketid || "NA"}
                    </Text>
                  </Flex>

                  <Text fontSize="sm" color="gray.700" mb={3} noOfLines={2}>
                    {ticket.comments || "No comments available"}
                  </Text>
                </Box>

                {/* Footer */}
                <Flex
                  justify="space-between"
                  align="center"
                  fontSize="sm"
                  color="gray.500"
                  borderTop="1px solid"
                  borderColor="gray.100"
                  pt={2}
                  mt="auto"
                >
                  <Text noOfLines={1}>{ticket.name || "NA"}</Text>
                  <Text>{new Date(ticket.created_at).toLocaleDateString()}</Text>
                </Flex>
              </Card>
            ))}
          </SimpleGrid>

          <Card
            alignItems={"center"}
            alignSelf={"end"}
            gap={3}
            flexDirection={"row"}
          >
            <FiChevronLeft
              cursor={page === 1 ? "not-allowed" : "pointer"}
              color={page === 1 ? "gray" : "#007bc9"}
              size={35}
              onClick={page === 1 ? null : onPrevious}
            />

            <Flex gap={3}>
              <Text>{initialValue}</Text>-
              <Text>
                {rows * page >= total_ticket_count
                  ? total_ticket_count
                  : rows * page}
              </Text>
              <Text>of</Text>
              <Text>{total_ticket_count}</Text>
            </Flex>

            <FiChevronRight
              cursor={"pointer"}
              color={rows * page >= total_ticket_count ? "gray" : "#007bc9"}
              size={35}
              onClick={onNext}
            />
          </Card>
        </>
      )}
    </>
  );
};

export default Support;
